import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createClassroom, deleteClassroom, updateClassroom, fetchClassrooms, fetchClassroomUsage, reassignClassroom } from '../api';
import { useTenantCode } from './useTenantCode';
import { invalidateAfterReassign } from './invalidations';

// `useTenantCode()` devuelve `string | null`, de ahí el null en la firma.
const invalidateClassroomQueries = (tenantCode: string | null | undefined) => {
    queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    if (tenantCode) {
        queryClient.invalidateQueries({ queryKey: ['tenant-branding', tenantCode] });
    }
};

export const useClassroomsQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['classrooms', tenantCode],
        queryFn: fetchClassrooms
    });
};

export const useCreateClassroomMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: createClassroom,
        onSuccess: () => {
            invalidateClassroomQueries(tenantCode);
            toast.success('Sala creada correctamente');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error creando la sala');
        },
    });
};

export const useUpdateClassroomMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: updateClassroom,
        // B1: renombrar una sala cascadea el nombre nuevo a las clases y a la
        // plantilla, así que hay que invalidar mucho más que ['classrooms'].
        onSuccess: (data) => {
            invalidateAfterReassign(tenantCode);

            // Cambiar el aforo de la sala lo lleva a sus clases futuras, pero las
            // que ya tienen más gente dentro de la que cabría se quedan como
            // estaban: hay que decirlo, o el centro cree que se aplicó a todas.
            const omitidas = data?.capacityStats?.skipped ?? [];
            const actualizadas = data?.capacityStats?.eventsUpdated ?? 0;

            if (omitidas.length > 0) {
                const fechas = omitidas
                    .slice(0, 5)
                    .map((e) => `${new Date(e.fecha).toLocaleDateString()} ${e.horaInicio}`)
                    .join(', ');
                toast.warn(
                    `Sala actualizada. El aforo nuevo se aplicó a ${actualizadas} clase(s), pero ${omitidas.length} se quedaron como estaban porque ya tienen más plazas ocupadas: ${fechas}${omitidas.length > 5 ? '…' : ''}`,
                    { autoClose: false, toastId: 'classroom-capacity-skipped' }
                );
                return;
            }

            toast.success(
                actualizadas > 0
                    ? `Sala actualizada. El aforo nuevo se aplicó a ${actualizadas} clase(s) futura(s).`
                    : 'Sala actualizada correctamente'
            );
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error actualizando la sala');
        },
    });
};

// B1 — uso de una sala, contado por nombre.
export const useClassroomUsageQuery = (classroomId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['classroom-usage', tenantCode, classroomId],
        queryFn: () => fetchClassroomUsage(classroomId as string),
        enabled: Boolean(classroomId),
        staleTime: 0,
    });
};

// B1 — reasignar las clases futuras de una sala a otra.
export const useReassignClassroomMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: ({ classroomId, targetId }: { classroomId: string; targetId: string }) =>
            reassignClassroom(classroomId, targetId),
        onSuccess: () => invalidateAfterReassign(tenantCode),
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error reasignando la sala');
        },
    });
};

// B1: puede acabar archivando en vez de borrando; el mensaje sale de la respuesta.
export const useDeleteClassroomMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: deleteClassroom,
        onSuccess: (response) => {
            invalidateAfterReassign(tenantCode);
            toast.success(response?.archived ? 'Sala archivada' : 'Sala eliminada');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error eliminando la sala');
        },
    });
};
